/**
 * CROEV Quiz V1 - Quiz Engine
 * Frøya Organics - Arctic Skin Transformation Quiz
 * 
 * Features:
 * - Multi-step navigation with state management
 * - Single and multi-select question handling
 * - Auto-advance for single-select questions
 * - Loading animations with timed transitions
 * - Dynamic content population based on answers
 * - URL parameter generation for redirect
 * - localStorage backup of answers
 */

(function() {
  'use strict';

  // Age mapping for calculations
  const AGE_MAP = {
    'under_35': 30,
    '35_44': 40,
    '45_54': 50,
    '55_64': 60,
    'over_65': 70
  };

  // Longest the direct-to-checkout path waits for the quiz cart attributes
  // after its add-to-cart. The request normally finishes during the add.
  const TAG_WAIT_MS = 800;

  // Age order for finding younger age (for comparison step)
  const AGE_ORDER = ['under_35', '35_44', '45_54', '55_64', 'over_65'];

  // Problem area labels for display
  const PROBLEM_LABELS = {
    'forehead': 'Forehead Lines',
    'crows_feet': "Crow's Feet",
    'dark_circles': 'Dark Circles',
    'smile_lines': 'Smile Lines',
    'sagging': 'Sagging',
    'uneven_tone': 'Uneven Tone'
  };

  class CroevQuiz {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.redirectUrl = container.dataset.redirectUrl;
      
      // Get quiz data from JSON
      const dataEl = document.getElementById(`croev-quiz-data-${this.sectionId}`);
      this.config = dataEl ? JSON.parse(dataEl.textContent) : {};
      
      // State
      this.state = {
        currentStep: 0,
        answers: {},
        history: []
      };
      
      // DOM elements
      this.steps = Array.from(container.querySelectorAll('.croev-quiz__step'));
      this.progressBar = container.querySelector('.croev-quiz__progress-bar');
      this.progressContainer = container.querySelector('.croev-quiz__progress');
      
      // Timers
      this.autoAdvanceTimer = null;
      this.checklistTimers = [];

      // Tracking
      this.sessionId = this.generateSessionId();
      this.utmParams = this.captureUTMs();
      this.startTime = Date.now();
      this.webhookUrl = container.dataset.webhookUrl || '';

      // Mobile nav relocation state
      this._movedNav = null;
      this._movedNavParent = null;

      this.init();
    }

    init() {
      // Clear saved state on fresh page load (start quiz fresh each time)
      this.clearState();

      // Bind events
      this.bindEvents();

      // Show first step
      this.showStep(this.state.currentStep);

      // Update progress
      this.updateProgress();
    }

    bindEvents() {
      // Option selection
      this.container.addEventListener('click', (e) => {
        const option = e.target.closest('.croev-quiz__option');
        if (option) {
          this.handleOptionSelect(option);
        }
        
        const continueBtn = e.target.closest('[data-action="continue"]');
        if (continueBtn) {
          this.nextStep();
        }
        
        const backBtn = e.target.closest('[data-action="back"]');
        if (backBtn) {
          this.prevStep();
        }
        
        const claimBtn = e.target.closest('[data-action="claim-offer"]');
        if (claimBtn) {
          this.handleClaimOffer(claimBtn);
        }

        const skipBtn = e.target.closest('[data-action="skip"]');
        if (skipBtn) {
          this.handleSkip(skipBtn);
        }
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const option = e.target.closest('.croev-quiz__option');
          if (option) {
            e.preventDefault();
            this.handleOptionSelect(option);
          }
        }
      });

      // Input field handling
      this.container.addEventListener('input', (e) => {
        const input = e.target.closest('.croev-quiz__input');
        if (input) {
          this.handleInputChange(input);
        }
      });

      // Input field Enter key to continue
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const input = e.target.closest('.croev-quiz__input');
          if (input) {
            const step = input.closest('.croev-quiz__step');
            const continueBtn = step ? this.getContinueBtn(step) : null;
            if (continueBtn && !continueBtn.disabled) {
              this.nextStep();
            }
          }
        }
      });
    }

    handleInputChange(input) {
      const step = input.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const value = input.value.trim();
      const inputType = input.type;
      const min = input.min ? parseInt(input.min, 10) : null;
      const max = input.max ? parseInt(input.max, 10) : null;
      const required = input.hasAttribute('required');

      // Validate
      let isValid = true;
      const errorEl = step.querySelector('.croev-quiz__input-error');

      if (required && !value) {
        isValid = false;
      } else if (inputType === 'number' && value) {
        const numVal = parseInt(value, 10);
        if (isNaN(numVal)) {
          isValid = false;
        } else if (min !== null && numVal < min) {
          isValid = false;
        } else if (max !== null && numVal > max) {
          isValid = false;
        }
      } else if (inputType === 'email' && value) {
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      // Update UI
      input.classList.toggle('is-invalid', !isValid && value.length > 0);
      if (errorEl) {
        errorEl.hidden = isValid || value.length === 0;
      }

      // Store answer if valid
      if (isValid && value) {
        this.state.answers[questionId] = { value, label: value };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }

      // Enable/disable continue button
      const continueBtn = this.getContinueBtn(step);
      if (continueBtn) {
        continueBtn.disabled = !isValid || !value;
      }
    }

    handleOptionSelect(option) {
      const step = option.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const selectionType = step.dataset.selectionType;
      const autoAdvance = step.dataset.autoAdvance === 'true';
      const value = option.dataset.optionValue;
      const label = option.dataset.optionLabel;

      if (selectionType === 'single') {
        // Deselect all others
        step.querySelectorAll('.croev-quiz__option').forEach(opt => {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-pressed', 'false');
        });
        
        // Select this one
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');
        
        // Store answer
        this.state.answers[questionId] = { value, label };
        this.saveState();
        
        // Auto-advance if enabled
        if (autoAdvance) {
          setTimeout(() => this.nextStep(), 300);
        } else {
          // Enable continue button
          const continueBtn = this.getContinueBtn(step);
          if (continueBtn) continueBtn.disabled = false;
        }
      } else {
        // Multi-select toggle
        option.classList.toggle('is-selected');
        const isSelected = option.classList.contains('is-selected');
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        
        // Gather all selected values
        const selectedOptions = Array.from(step.querySelectorAll('.croev-quiz__option.is-selected'));
        this.state.answers[questionId] = selectedOptions.map(opt => ({
          value: opt.dataset.optionValue,
          label: opt.dataset.optionLabel
        }));
        this.saveState();
        
        // Enable/disable continue button based on selection
        const continueBtn = this.getContinueBtn(step);
        if (continueBtn) {
          continueBtn.disabled = selectedOptions.length === 0;
        }
      }

      // Track answer
      this.trackEvent('quiz_answer', {
        quiz_step_number: this.state.currentStep + 1,
        quiz_question_id: questionId,
        quiz_answer_value: this.getAnswerValue(questionId)
      });
    }

    showStep(index) {
      // Clear any pending timers
      this.clearTimers();
      
      // Hide all steps
      this.steps.forEach((step, i) => {
        if (i === index) {
          step.classList.add('is-active');
          step.classList.remove('is-exiting');
        } else {
          step.classList.remove('is-active');
        }
      });

      // Mobile: move nav outside the scroll container so it's always visible
      this.relocateNav(this.steps[index]);

      // Measure the nav/CTA bar height and set CSS variable for wrapper padding
      this.updateNavHeight(this.steps[index]);

      const currentStep = this.steps[index];
      if (!currentStep) return;

      const stepType = currentStep.dataset.stepType;

      // Track step view
      const stepName = currentStep.dataset.questionId || stepType;
      this.trackEvent('quiz_step_view', {
        quiz_step_number: index + 1,
        quiz_step_name: stepName,
        quiz_total_steps: this.steps.length
      });

      // Track quiz start on first step (fresh session only)
      if (index === 0 && this.state.history.length === 0) {
        this.trackEvent('quiz_start', {
          quiz_total_steps: this.steps.length,
          ...this.utmParams
        });
        this.quizTrack('quiz_started');
      }

      // Focus management
      setTimeout(() => {
        const inputField = currentStep.querySelector('.croev-quiz__input');
        const focusTarget = inputField || currentStep.querySelector('.croev-quiz__option, .croev-quiz__btn--primary');
        if (focusTarget) focusTarget.focus();
      }, 100);

      // Restore selections for question steps
      if (stepType === 'question_image' || stepType === 'question_text') {
        this.restoreSelections(currentStep);
      }

      // Restore and handle input question steps
      if (stepType === 'question_input') {
        this.restoreInputValue(currentStep);
      }

      // Handle dynamic content for summary steps
      if (stepType === 'summary_profile') {
        this.populateSummaryProfile(currentStep);
      }

      if (stepType === 'summary_results') {
        this.populateSummaryResults(currentStep);
        this.reportResult();
      }

      if (stepType === 'summary_comparison') {
        this.populateSummaryComparison(currentStep);
      }

      // Handle loading steps
      if (stepType === 'loading_step') {
        this.handleLoadingStep(currentStep);
      }

      // Handle info steps with dynamic content
      if (stepType === 'info_step') {
        this.populateInfoStep(currentStep);
      }

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const wrapper = this.container.querySelector('.croev-quiz__wrapper');
      if (wrapper) wrapper.scrollTop = 0;
    }

    restoreSelections(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      
      if (!answer) return;

      const isMulti = step.dataset.selectionType === 'multi';
      const values = isMulti ? answer.map(a => a.value) : [answer.value];

      step.querySelectorAll('.croev-quiz__option').forEach(option => {
        const optValue = option.dataset.optionValue;
        if (values.includes(optValue)) {
          option.classList.add('is-selected');
          option.setAttribute('aria-pressed', 'true');
        }
      });

      // Enable continue button if has selection
      const continueBtn = this.getContinueBtn(step);
      if (continueBtn && values.length > 0) {
        continueBtn.disabled = false;
      }
    }

    restoreInputValue(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      const input = step.querySelector('.croev-quiz__input');

      if (!answer || !input) return;

      input.value = answer.value || '';

      // Trigger validation to enable continue button if valid
      if (input.value) {
        this.handleInputChange(input);
      }
    }

    nextStep() {
      // Track input step answer before advancing
      const currentStepEl = this.steps[this.state.currentStep];
      if (currentStepEl && currentStepEl.dataset.questionId && this.state.currentStep < this.steps.length - 1) {
        // quiz_step: every forward commit off a question step (answer is null
        // when the step was skipped), so drop-off shows at which question.
        this.quizTrack('quiz_step', {
          step_index: this.state.currentStep + 1,
          question_id: currentStepEl.dataset.questionId,
          answer: this.getAnswersFlat()[currentStepEl.dataset.questionId] || null
        });
      }
      if (currentStepEl) {
        const currentStepType = currentStepEl.dataset.stepType;
        const currentQuestionId = currentStepEl.dataset.questionId;
        if (currentStepType === 'question_input' && currentQuestionId && this.state.answers[currentQuestionId]) {
          this.trackEvent('quiz_answer', {
            quiz_step_number: this.state.currentStep + 1,
            quiz_question_id: currentQuestionId,
            quiz_answer_value: this.state.answers[currentQuestionId].value || ''
          });
        }
      }

      if (this.state.currentStep < this.steps.length - 1) {
        this.state.history.push(this.state.currentStep);
        this.state.currentStep++;
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    prevStep() {
      if (this.state.history.length > 0) {
        this.state.currentStep = this.state.history.pop();
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    updateNavHeight(step) {
      if (!step) return;
      requestAnimationFrame(() => {
        let nav = step.querySelector('.croev-quiz__nav') || step.querySelector('.croev-quiz__offer-cta-bar');
        if (!nav && this._movedNav) nav = this._movedNav;
        const wrapper = this.container.querySelector('.croev-quiz__wrapper');
        if (wrapper) {
          const h = (nav && nav !== this._movedNav) ? nav.offsetHeight : 0;
          wrapper.style.setProperty('--quiz-nav-height', h + 'px');
        }
      });
    }

    relocateNav(activeStep) {
      if (window.innerWidth > 740) return;

      // Restore previously relocated nav to its original step
      if (this._movedNav && this._movedNavParent) {
        this._movedNavParent.appendChild(this._movedNav);
        this._movedNav = null;
        this._movedNavParent = null;
      }

      const nav = activeStep.querySelector('.croev-quiz__nav');
      if (!nav) return;

      // Don't relocate hidden navs (auto-advance steps)
      if (getComputedStyle(nav).display === 'none') return;

      this._movedNavParent = nav.parentElement;
      this._movedNav = nav;
      this.container.appendChild(nav);
    }

    getContinueBtn(step) {
      let btn = step.querySelector('[data-action="continue"]');
      if (!btn && this._movedNav) {
        btn = this._movedNav.querySelector('[data-action="continue"]');
      }
      return btn;
    }

    updateProgress() {
      if (!this.progressBar) return;
      
      const progress = ((this.state.currentStep + 1) / this.steps.length) * 100;
      this.progressBar.style.width = `${progress}%`;
      
      if (this.progressContainer) {
        this.progressContainer.setAttribute('aria-valuenow', Math.round(progress));
      }
    }

    // --- Dynamic Content Population ---

    getPerceivedAge() {
      // Prefer exact age from input, fall back to Q1 age range
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;

      let baseAge;
      if (exactAge && exactAge.value) {
        baseAge = parseInt(exactAge.value, 10);
      } else if (ageRangeAnswer) {
        baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      } else {
        return null;
      }

      // Perceived age = actual age + 10 years (skin makes them look older)
      return baseAge + 10;
    }

    getGoalAge() {
      // Prefer exact age from input, fall back to Q1 age range
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;

      let baseAge;
      if (exactAge && exactAge.value) {
        baseAge = parseInt(exactAge.value, 10);
      } else if (ageRangeAnswer) {
        baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      } else {
        return null;
      }

      // Goal age = actual age - 8 years (look younger than their age)
      return baseAge - 8;
    }

    getProblemAreasText(limit = 2) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return 'your concerns';
      
      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return labels.join(' & ');
    }

    getDatePlus(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    populateSummaryProfile(step) {
      const perceivedAge = this.getPerceivedAge();
      const problemAreasText = this.getProblemAreasText();

      // Update perceived age
      const ageEl = step.querySelector('[data-field="perceived_age"]');
      if (ageEl && perceivedAge) {
        ageEl.textContent = `${perceivedAge} years`;
      }

      // Update problem areas
      const problemEl = step.querySelector('[data-field="problem_areas"]');
      if (problemEl) {
        problemEl.textContent = problemAreasText;
      }

      // Update alert message
      const alertEl = step.querySelector('.croev-quiz__alert');
      if (alertEl) {
        const template = alertEl.dataset.alertTemplate || '';
        const message = template
          .replace('{perceived_age}', perceivedAge || '--')
          .replace('{problem_areas}', problemAreasText);
        const alertP = alertEl.querySelector('p');
        if (alertP) alertP.innerHTML = message;
      }

      // Update problem areas visual
      const problemAreasVisual = step.querySelector('.croev-quiz__problem-areas-visual');
      const problemAreas = this.state.answers.problem_areas;

      console.log('Problem areas visual element:', problemAreasVisual);
      console.log('Problem areas answers:', problemAreas);
      console.log('Dataset:', problemAreasVisual?.dataset);

      if (problemAreasVisual && problemAreas && Array.isArray(problemAreas) && problemAreas.length > 0) {
        // Show up to 3 problem area images
        const selectedAreas = problemAreas.slice(0, 3);
        let html = '<div class="croev-quiz__problem-areas-grid">';

        // Direct mapping for dataset keys (simplified attribute names without underscores)
        const imageMap = {
          'forehead': problemAreasVisual.dataset.imgForehead,
          'crows_feet': problemAreasVisual.dataset.imgCrowsfeet,
          'dark_circles': problemAreasVisual.dataset.imgDarkcircles,
          'smile_lines': problemAreasVisual.dataset.imgSmilelines,
          'sagging': problemAreasVisual.dataset.imgSagging,
          'uneven_tone': problemAreasVisual.dataset.imgUneventone
        };

        const labelMap = {
          'forehead': 'Forehead Lines',
          'crows_feet': "Crow's Feet",
          'dark_circles': 'Dark Circles',
          'smile_lines': 'Smile Lines',
          'sagging': 'Sagging',
          'uneven_tone': 'Uneven Tone'
        };

        console.log('Image map:', imageMap);

        selectedAreas.forEach(area => {
          const imageUrl = imageMap[area.value];
          const label = labelMap[area.value] || PROBLEM_LABELS[area.value] || area.label;

          console.log(`Area: ${area.value}, Image URL: ${imageUrl}`);

          if (imageUrl && imageUrl.length > 10) {
            html += `
              <div class="croev-quiz__problem-area-item">
                <img src="${imageUrl}" alt="${label}" class="croev-quiz__problem-area-img" loading="lazy">
                <span class="croev-quiz__problem-area-label">${label}</span>
              </div>
            `;
          }
        });

        html += '</div>';
        problemAreasVisual.innerHTML = html;
      } else {
        console.log('Skipping problem areas visual - conditions not met');
      }
    }

    populateSummaryResults(step) {
      const date30 = this.getDatePlus(30);
      const date60 = this.getDatePlus(60);

      step.querySelectorAll('[data-template]').forEach(el => {
        const template = el.dataset.template;
        const content = template
          .replace('{date_30}', date30)
          .replace('{date_60}', date60);
        el.innerHTML = content;
      });
    }

    populateSummaryComparison(step) {
      const perceivedAge = this.getPerceivedAge();
      const goalAge = this.getGoalAge();
      const ageAnswer = this.state.answers.age?.value;

      // Update ages
      const perceivedAgeEl = step.querySelector('[data-field="perceived_age"]');
      if (perceivedAgeEl && perceivedAge) {
        perceivedAgeEl.textContent = `${perceivedAge} years`;
      }

      const goalAgeEl = step.querySelector('[data-field="goal_age"]');
      if (goalAgeEl && goalAge) {
        goalAgeEl.textContent = `${goalAge} years`;
      }

      // Update years younger text
      const yearsYoungerEl = step.querySelector('[data-field="years_younger"]');
      if (yearsYoungerEl && perceivedAge && goalAge) {
        const yearsDiff = perceivedAge - goalAge;
        yearsYoungerEl.textContent = `${yearsDiff}+ years`;
      }

      // Update images using age images from quiz config
      const ageImages = this.config.ageImages || {};
      const nowImageUrl = ageAnswer ? ageImages[ageAnswer] : null;

      if (nowImageUrl && nowImageUrl.length > 0) {
        // NOW image = user's selected age
        const nowImg = step.querySelector('[data-image-type="now"]');
        if (nowImg) {
          nowImg.style.backgroundImage = `url('${nowImageUrl}')`;
          // Remove placeholder
          const placeholder = nowImg.querySelector('.croev-quiz__comparison-card-placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }

        // GOAL image = younger age bracket (2 steps younger, min is under_35)
        // If already at youngest tier (under_35), use special "youngest_goal" image
        const currentAgeIndex = AGE_ORDER.indexOf(ageAnswer);
        const goalAgeIndex = Math.max(0, currentAgeIndex - 2);
        let goalImageUrl;

        if (currentAgeIndex === 0) {
          // User selected youngest tier - use the special youngest goal image
          goalImageUrl = ageImages['youngest_goal'];
        } else {
          const goalAgeKey = AGE_ORDER[goalAgeIndex];
          goalImageUrl = ageImages[goalAgeKey];
        }

        const goalImg = step.querySelector('[data-image-type="goal"]');
        if (goalImg && goalImageUrl && goalImageUrl.length > 0) {
          goalImg.style.backgroundImage = `url('${goalImageUrl}')`;
          // Remove placeholder
          const placeholder = goalImg.querySelector('.croev-quiz__comparison-card-placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }
      }
    }

    populateInfoStep(step) {
      // Handle dynamic subheadline with {problem_areas} placeholder
      const subheadlineEl = step.querySelector('.croev-quiz__subheadline--dynamic');
      if (subheadlineEl) {
        const template = subheadlineEl.dataset.subheadlineTemplate || subheadlineEl.textContent;
        const problemAreasText = this.getProblemAreasTextBold();
        const content = template.replace('{problem_areas}', problemAreasText);
        subheadlineEl.innerHTML = content;
      }

      // Duplicate carousel images for seamless infinite scroll
      const carousel = step.querySelector('.croev-quiz__image-carousel');
      if (carousel && !carousel.dataset.duplicated) {
        const track = carousel.querySelector('.croev-quiz__image-carousel-track');
        if (track) {
          const items = track.innerHTML;
          track.innerHTML = items + items; // Duplicate for seamless loop
          carousel.dataset.duplicated = 'true';
        }
      }
    }

    getProblemAreasTextBold(limit = 3) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return '<strong>your concerns</strong>';

      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return '<strong>' + labels.join(' & ') + '</strong>';
    }

    handleLoadingStep(step) {
      const duration = parseInt(step.dataset.duration, 10) || 3000;
      const checklist = step.querySelector('.croev-quiz__checklist');

      if (checklist) {
        // Animate checklist items
        const items = Array.from(checklist.querySelectorAll('.croev-quiz__checklist-item'));
        items.forEach((item, i) => {
          const delay = parseInt(item.dataset.delay, 10) || i * 1000;
          
          const timer = setTimeout(() => {
            item.classList.add('is-visible');
            
            // Mark as complete after appearing
            setTimeout(() => {
              item.classList.add('is-complete');
            }, 500);
          }, delay);
          
          this.checklistTimers.push(timer);
        });
      }

      // Auto-advance after duration
      this.autoAdvanceTimer = setTimeout(() => {
        this.nextStep();
      }, duration);
    }

    clearTimers() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
      this.checklistTimers.forEach(t => clearTimeout(t));
      this.checklistTimers = [];
    }

    // --- Offer Handling ---

    handleSkip(btn) {
      // Skip this step without saving an answer
      const step = btn.closest('.croev-quiz__step');
      const questionId = step?.dataset.questionId;

      // Clear any answer for this question
      if (questionId && this.state.answers[questionId]) {
        delete this.state.answers[questionId];
        this.saveState();
      }

      // Move to next step
      this.nextStep();
    }

    async handleClaimOffer(btn) {
      const productUrl = btn.dataset.productUrl;
      const variantId = btn.dataset.variantId;
      const sellingPlanId = btn.dataset.sellingPlanId;
      const checkoutMode = btn.dataset.checkoutMode;
      const step = btn.closest('.croev-quiz__step');
      const discountCode = step?.dataset.discountCode;

      // Disable button to prevent double-clicks
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Adding to cart...';

      // Re-write the quiz attribution before this add. Started in parallel with
      // the add so it costs no time; the direct-checkout path waits for it at
      // most TAG_WAIT_MS after the add, so the checkout carries the attributes.
      // It always resolves, so a failure never blocks the checkout.
      const tagged = this.tagCart();

      // Fire tracking events
      const durationSeconds = Math.round((Date.now() - this.startTime) / 1000);

      this.trackEvent('quiz_complete', {
        quiz_duration_seconds: durationSeconds,
        quiz_steps_completed: this.state.history.length + 1
      });

      this.sendWebhook({
        event: 'quiz_complete',
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        answers: this.getAnswersFlat(),
        utm: this.utmParams,
        duration_seconds: durationSeconds,
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        steps_completed: this.state.history.length + 1,
        total_steps: this.steps.length
      });

      // Direct-to-checkout mode: add subscription to cart via AJAX, then go to checkout
      if (checkoutMode === 'direct' && variantId) {
        try {
          const cartBody = {
            items: [{
              id: parseInt(variantId),
              quantity: 1
            }]
          };

          // Attach selling plan for subscription pricing
          if (sellingPlanId) {
            cartBody.items[0].selling_plan = parseInt(sellingPlanId);
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartBody)
          });

          if (!response.ok) {
            throw new Error('Cart add failed');
          }

          await Promise.race([tagged, new Promise((resolve) => setTimeout(resolve, TAG_WAIT_MS))]);

          // Redirect to checkout with discount code
          if (discountCode) {
            window.location.href = `/discount/${discountCode}?redirect=/checkout`;
          } else {
            window.location.href = '/checkout';
          }
          return;
        } catch (err) {
          console.error('Direct checkout failed, falling back to product page:', err);
          // Fall through to product page redirect below
          btn.disabled = false;
          btn.textContent = originalText;
        }
      }

      // Fallback: redirect to product page (original behavior)
      const params = new URLSearchParams();
      Object.entries(this.state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(key, value.map(v => v.value).join(','));
        } else if (value && value.value) {
          params.set(key, value.value);
        }
      });

      if (variantId) {
        params.set('variant', variantId);
      }

      let url = this.redirectUrl || productUrl;
      if (url) {
        const separator = url.includes('?') ? '&' : '?';
        const destinationUrl = `${url}${separator}${params.toString()}`;

        if (discountCode) {
          window.location.href = `/discount/${discountCode}?redirect=${encodeURIComponent(destinationUrl)}`;
        } else {
          window.location.href = destinationUrl;
        }
      }
    }

    // --- State Persistence ---

    saveState() {
      try {
        localStorage.setItem(`croev-quiz-${this.sectionId}`, JSON.stringify(this.state));
      } catch (e) {
        console.warn('Could not save quiz state:', e);
      }
    }

    loadState() {
      try {
        const saved = localStorage.getItem(`croev-quiz-${this.sectionId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Only restore answers, start from beginning
          this.state.answers = parsed.answers || {};
        }
      } catch (e) {
        console.warn('Could not load quiz state:', e);
      }
    }

    clearState() {
      try {
        localStorage.removeItem(`croev-quiz-${this.sectionId}`);
      } catch (e) {
        console.warn('Could not clear quiz state:', e);
      }
    }

    // --- Tracking ---

    generateSessionId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    }

    captureUTMs() {
      const params = new URLSearchParams(window.location.search);
      const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'];
      const utm = {};
      keys.forEach(key => {
        const val = params.get(key);
        if (val) utm[key] = val;
      });

      try {
        const stored = JSON.parse(localStorage.getItem(`croev-quiz-utm-${this.sectionId}`) || '{}');
        const merged = { ...stored, ...utm };
        if (Object.keys(merged).length > 0) {
          localStorage.setItem(`croev-quiz-utm-${this.sectionId}`, JSON.stringify(merged));
        }
        return merged;
      } catch (e) {
        return utm;
      }
    }

    trackEvent(eventName, eventData) {
      const params = {
        quiz_name: 'skin_transformation',
        quiz_session_id: this.sessionId,
        ...eventData
      };

      // Send directly to GA4 via gtag
      if (typeof gtag === 'function') {
        gtag('event', eventName, params);
      }

      // Also push to dataLayer for GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
    }

    // --- Quiz events + order attribution ---
    // window.FroyaQuiz comes from snippets/quiz-analytics.liquid (rendered by
    // the section before this script). This section serves two pages, so the id
    // comes from the template. Without the helper every call is a no-op.

    quizTrack(eventName, payload) {
      const quiz = window.FroyaQuiz;
      if (!quiz) return;
      quiz.track(eventName, Object.assign({ quiz_id: quiz.idForTemplate() }, payload || {}));
    }

    // The result is the offer step's product (one fixed product per template),
    // recorded by variant id.
    offerVariantId() {
      const btn = this.container.querySelector('[data-action="claim-offer"]');
      return btn ? btn.dataset.variantId || null : null;
    }

    tagCart() {
      const quiz = window.FroyaQuiz;
      if (!quiz) return Promise.resolve();
      return quiz.tagCart(quiz.idForTemplate(), this.offerVariantId());
    }

    // At the result reveal (summary_results), once per page load — Back/Next
    // can show that step again.
    reportResult() {
      if (this._resultReported) return;
      this._resultReported = true;
      this.tagCart();
      this.quizTrack('quiz_completed', {
        result: this.offerVariantId(),
        answers: this.getAnswersFlat()
      });
    }

    sendWebhook(data) {
      if (!this.webhookUrl) return;

      const payload = JSON.stringify(data);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(this.webhookUrl, new Blob([payload], { type: 'text/plain' }));
      } else {
        fetch(this.webhookUrl, {
          method: 'POST',
          body: payload,
          mode: 'no-cors',
          keepalive: true
        }).catch(() => {});
      }
    }

    getAnswerValue(questionId) {
      const answer = this.state.answers[questionId];
      if (!answer) return '';
      if (Array.isArray(answer)) {
        return answer.map(a => a.value).join(',');
      }
      return answer.value || '';
    }

    getAnswersFlat() {
      const flat = {};
      Object.entries(this.state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          flat[key] = value.map(v => v.value).join(',');
        } else if (value && value.value) {
          flat[key] = value.value;
        }
      });
      return flat;
    }

    // --- Utilities ---

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    destroy() {
      this.clearTimers();
    }
  }

  // Initialize all quiz instances
  function initQuizzes() {
    document.querySelectorAll('[data-section-type="croev-quiz"]').forEach(container => {
      if (!container.croevQuiz) {
        container.croevQuiz = new CroevQuiz(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzes);
  } else {
    initQuizzes();
  }

  // Handle Shopify theme editor events
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container) {
      container.croevQuiz = new CroevQuiz(container);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container && container.croevQuiz) {
      container.croevQuiz.destroy();
    }
  });

})();
